import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { ActiveLayer } from '@okmaps/shared';

/**
 * The layers currently on the map, in draw order.
 *
 * This slice is pure state: it never talks to the network and never touches the
 * ArcGIS SDK. `src/map/esriSync.ts` observes it and issues the imperative SDK
 * calls. Keeping the ordering authoritative here (rather than reading it back
 * off the SDK's layer collection) is what makes the layer list reorderable
 * without the map and the UI drifting apart.
 *
 * Index 0 is the bottom of the stack, matching the SDK's own convention.
 */
export interface LayersState {
  readonly active: ActiveLayer[];
  /** Layer name whose settings panel is open, if any. */
  readonly focused: string | null;
}

const initialState: LayersState = { active: [], focused: null };

const layersSlice = createSlice({
  name: 'layers',
  initialState,
  reducers: {
    layerAdded: {
      reducer(state, action: PayloadAction<ActiveLayer>) {
        // Adding a layer already on the map is a no-op rather than a duplicate.
        if (state.active.some((l) => l.name === action.payload.name)) return;
        state.active.push(action.payload);
        state.focused = action.payload.name;
      },
      prepare(layer: Omit<ActiveLayer, 'opacity' | 'visible'>) {
        return { payload: { ...layer, opacity: 1, visible: true } };
      },
    },

    layerRemoved(state, action: PayloadAction<string>) {
      state.active = state.active.filter((l) => l.name !== action.payload);
      if (state.focused === action.payload) state.focused = null;
    },

    layerVisibilityToggled(state, action: PayloadAction<string>) {
      const layer = state.active.find((l) => l.name === action.payload);
      if (layer) layer.visible = !layer.visible;
    },

    layerOpacityChanged(
      state,
      action: PayloadAction<{ name: string; opacity: number }>,
    ) {
      const layer = state.active.find((l) => l.name === action.payload.name);
      // Clamp rather than trust the caller; the slider is not the only caller.
      if (layer) layer.opacity = Math.min(1, Math.max(0, action.payload.opacity));
    },

    /** Move a layer within the draw order. Out-of-range indices are ignored. */
    layerReordered(
      state,
      action: PayloadAction<{ from: number; to: number }>,
    ) {
      const { from, to } = action.payload;
      const last = state.active.length - 1;
      if (from < 0 || to < 0 || from > last || to > last || from === to) return;
      const [moved] = state.active.splice(from, 1);
      if (moved) state.active.splice(to, 0, moved);
    },

    layerFocused(state, action: PayloadAction<string | null>) {
      state.focused = action.payload;
    },

    allLayersCleared(state) {
      state.active = [];
      state.focused = null;
    },
  },
});

export const {
  layerAdded,
  layerRemoved,
  layerVisibilityToggled,
  layerOpacityChanged,
  layerReordered,
  layerFocused,
  allLayersCleared,
} = layersSlice.actions;

export default layersSlice.reducer;
